import { useState, useRef, useEffect } from 'react';

const NICKNAME_MIN = 2;
const NICKNAME_MAX = 20;

export interface ProfileData {
  nickname: string;
  avatarUrl: string | null;
  avatarColor: string;
}

interface EditProfileSheetProps {
  open: boolean;
  profile: ProfileData;
  email: string;
  onSave: (data: ProfileData) => Promise<void>;
  onClose: () => void;
}

function getInitial(name: string, email: string): string {
  if (name && name.trim()) return name.trim().charAt(0).toUpperCase();
  if (email) return email.charAt(0).toUpperCase();
  return 'U';
}

export default function EditProfileSheet({ open, profile, email, onSave, onClose }: EditProfileSheetProps) {
  const [nickname, setNickname] = useState(profile.nickname);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(profile.avatarUrl);
  const [previewFile, setPreviewFile] = useState<string | null>(profile.avatarUrl);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset local state when sheet opens
  useEffect(() => {
    if (open) {
      setNickname(profile.nickname);
      setAvatarUrl(profile.avatarUrl);
      setPreviewFile(profile.avatarUrl);
      setError('');
    }
  }, [open, profile]);

  // Toast auto-dismiss
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 2000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const validateNickname = (v: string): string => {
    const t = v.trim();
    if (!t) return '昵称不能为空';
    if (t.length < NICKNAME_MIN) return `至少 ${NICKNAME_MIN} 个字符`;
    if (t.length > NICKNAME_MAX) return `最多 ${NICKNAME_MAX} 个字符`;
    return '';
  };

  const handleNicknameChange = (v: string) => {
    setNickname(v);
    setError(validateNickname(v));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setPreviewFile(dataUrl);
      setAvatarUrl(dataUrl);
    };
    reader.readAsDataURL(file);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleRemoveAvatar = () => {
    setError('当前版本仅支持更换头像，不支持删除头像');
  };

  const hasChanges = (): boolean => {
    if (nickname.trim() !== profile.nickname) return true;
    if (avatarUrl !== profile.avatarUrl) return true;
    return false;
  };

  const canSave = (): boolean => {
    if (saving) return false;
    if (validateNickname(nickname) !== '') return false;
    if (!hasChanges()) return false;
    return true;
  };

  const handleSave = async () => {
    const validationError = validateNickname(nickname);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!hasChanges()) return;

    setSaving(true);
    try {
      await onSave({
        nickname: nickname.trim(),
        avatarUrl,
        avatarColor: profile.avatarColor,
      });
      setToast('资料已保存');
      // Sheet closes after brief toast
      setTimeout(() => onClose(), 800);
    } catch {
      setError('保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const initial = getInitial(nickname, email);
  const nickErr = validateNickname(nickname);

  return (
    <div className="fixed inset-0 z-[100] flex flex-col justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={saving ? undefined : onClose} />

      {/* Sheet */}
      <div
        className="relative bg-white rounded-t-3xl flex flex-col"
        style={{
          maxHeight: '90vh',
          paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)',
        }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-4">
          <h2 className="text-gray-900 font-bold text-lg">编辑资料</h2>
          <button
            onClick={onClose}
            disabled={saving}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100"
          >
            <i className="ri-close-line text-gray-500 text-sm" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 flex flex-col items-center gap-6">
          {/* Avatar preview */}
          <div className="relative">
            {previewFile ? (
              <div
                className="w-24 h-24 rounded-full bg-cover bg-center shadow-md"
                style={{ backgroundImage: `url(${previewFile})` }}
              />
            ) : (
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center shadow-md"
                style={{ background: profile.avatarColor }}
              >
                <span className="text-white text-4xl font-bold">{initial}</span>
              </div>
            )}
            {/* Camera overlay */}
            {!previewFile && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute -bottom-1 -right-1 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md"
              >
                <i className="ri-camera-fill text-sm" style={{ color: profile.avatarColor }} />
              </button>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3 w-full max-w-[260px]">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-medium transition-colors"
              style={{
                background: '#F3F4F6',
                color: '#374151',
              }}
            >
              <i className="ri-image-add-line text-base" />
              选择照片
            </button>
            {previewFile && (
              <button
                onClick={handleRemoveAvatar}
                className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
                style={{
                  background: '#FEF2F2',
                  color: '#EF4444',
                }}
              >
                <i className="ri-delete-bin-line text-base" />
                移除
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Divider */}
          <div className="w-full border-t border-gray-100" />

          {/* Nickname input */}
          <div className="w-full max-w-[260px]">
            <label className="block text-gray-500 text-xs font-medium mb-2">昵称</label>
            <div className="relative">
              <input
                type="text"
                value={nickname}
                onChange={(e) => handleNicknameChange(e.target.value)}
                maxLength={NICKNAME_MAX + 5}
                placeholder="输入昵称"
                className={`w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-colors ${
                  nickErr && nickname.trim().length > 0
                    ? 'bg-red-50 text-red-600'
                    : 'bg-gray-50 text-gray-900 focus:bg-indigo-50'
                }`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs">
                {nickname.trim().length}/{NICKNAME_MAX}
              </span>
            </div>
            {nickErr && nickname.trim().length > 0 && (
              <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                <i className="ri-error-warning-line text-[11px]" />
                {nickErr}
              </p>
            )}
            {!nickErr && nickname.trim().length > 0 && (
              <p className="text-gray-400 text-xs mt-1.5">
                {NICKNAME_MIN}-{NICKNAME_MAX} 个字符，支持中文/英文/数字
              </p>
            )}
          </div>
        </div>

        {/* Save button */}
        <div className="px-5 pt-5">
          <button
            onClick={handleSave}
            disabled={!canSave()}
            className="w-full py-3.5 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 transition-opacity"
            style={{
              background: canSave()
                ? 'linear-gradient(135deg, #6366F1, #7C3AED)'
                : '#D1D5DB',
              opacity: canSave() ? 1 : 0.7,
            }}
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                保存中...
              </>
            ) : (
              '保存'
            )}
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[110] px-5 py-3 bg-gray-900 text-white text-sm font-medium rounded-2xl shadow-xl flex items-center gap-2">
          <i className="ri-check-line text-green-400" />
          {toast}
        </div>
      )}
    </div>
  );
}
